import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAllCompanies, Company, getCompanyById, getCompanyProgramStats, toggleArchiveCompany, togglePinCompany } from "../../utils/api/endpoints/companies";
import { MdBusiness, MdSearch, MdFilterList, MdArchive, MdAutorenew } from "react-icons/md";
import ProgramsIcon from "../../assets/icons/ProgramsIcon";
import CompanyCard from "../../components/programs/CompanyCard";
import CompanyDashboard from "./CompanyDashboard";
import usePageTitle from "../../utils/hooks/usePageTitle";
import OrganizationsSubscriptions from "../admin/OrganizationsSubscriptions";
import CompanyProgramsTableTab from "../../components/companies/CompanyProgramsTableTab";

const AdminCompanyPrograms = () => {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId?: string }>();
  const [activeTab, setActiveTab] = useState<"companies" | "subscriptions" | "programs">("companies");
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [programCounts, setProgramCounts] = useState<Record<number, number>>({});
  const [latestProgramEndDates, setLatestProgramEndDates] = useState<Record<number, string | null>>({});
  const [earliestProgramStartDates, setEarliestProgramStartDates] = useState<Record<number, string | null>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>("all");
  const [customSubscription, setCustomSubscription] = useState<string>("");
  const [showArchivedOnly, setShowArchivedOnly] = useState<boolean>(false);
  const [programStatusFilter, setProgramStatusFilter] = useState<string>("all"); // all, active, expired

  usePageTitle("Companies");

  // Handle direct tab navigation via query parameter (?tab=programs)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tabQuery = searchParams.get("tab");
    if (tabQuery === "programs" || tabQuery === "subscriptions" || tabQuery === "companies") {
      setActiveTab(tabQuery as any);
    }
  }, []);

  // Debounce search query to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load company from URL parameter
  useEffect(() => {
    const loadCompanyFromUrl = async () => {
      if (companyId) {
        try {
          const company = await getCompanyById(parseInt(companyId));
          if (company) {
            setSelectedCompany(company);
            sessionStorage.setItem("ctb_admin_active_company_id", String(company.company_id));
            sessionStorage.setItem("ctb_admin_active_company_name", company.company_name);
          } else {
            // Company not found, redirect to list
            navigate("/dashboard/companies", { replace: true });
          }
        } catch (error) {
          console.error("Error loading company:", error);
          navigate("/dashboard/companies", { replace: true });
        }
      } else {
        setSelectedCompany(null);
      }
    };

    loadCompanyFromUrl();
  }, [companyId, navigate]);

  // Fetch companies and program counts (OPTIMIZED)
  useEffect(() => {
    const fetchCompaniesData = async () => {
      // Only fetch if not viewing a specific company
      if (companyId) return;

      setCompaniesLoading(true);
      try {
        // Fetch companies and program stats in parallel for maximum performance
        const [companiesResult, programStats] = await Promise.all([
          getAllCompanies(1, 1000, debouncedSearchQuery, showArchivedOnly),
          getCompanyProgramStats(debouncedSearchQuery)
        ]);

        if (companiesResult) {
          setCompanies(companiesResult.companies);

          // Extract program counts, end dates, and start dates from the optimized stats endpoint
          const counts: Record<number, number> = {};
          const endDates: Record<number, string | null> = {};
          const startDates: Record<number, string | null> = {};

          if (programStats) {
            companiesResult.companies.forEach(company => {
              const stats = programStats[company.company_id];
              if (stats) {
                counts[company.company_id] = stats.programCount;
                endDates[company.company_id] = stats.latestProgramEndDate;
                startDates[company.company_id] = stats.earliestProgramStartDate;
              } else {
                counts[company.company_id] = 0;
                endDates[company.company_id] = null;
                startDates[company.company_id] = null;
              }
            });
          } else {
            // Fallback if stats endpoint fails
            companiesResult.companies.forEach(company => {
              counts[company.company_id] = 0;
              endDates[company.company_id] = null;
              startDates[company.company_id] = null;
            });
          }

          setProgramCounts(counts);
          setLatestProgramEndDates(endDates);
          setEarliestProgramStartDates(startDates);
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompaniesData();
  }, [companyId, debouncedSearchQuery, showArchivedOnly]);

  // Handle company selection
  const handleCompanyClick = (company: Company) => {
    sessionStorage.setItem("ctb_admin_active_company_id", String(company.company_id));
    sessionStorage.setItem("ctb_admin_active_company_name", company.company_name);
    navigate(`/dashboard/companies/${company.company_id}`);
  };

  // Handle back to companies
  const handleBackToCompanies = () => {
    navigate("/dashboard/companies");
  };

  // Handle archive toggle
  const handleArchiveToggle = async (companyId: number, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    const result = await toggleArchiveCompany(companyId, newStatus);

    if (result) {
      // Remove the company from the list immediately for better UX
      // It will be filtered out based on the current archive filter
      setCompanies(prevCompanies =>
        prevCompanies.filter(company => {
          if (company.company_id === companyId) {
            // If we're showing archived only and we just unarchived, remove it
            if (showArchivedOnly && !newStatus) return false;
            // If we're showing non-archived and we just archived, remove it
            if (!showArchivedOnly && newStatus) return false;
          }
          return true;
        })
      );
    }
  };

  // Handle pin toggle
  const handlePinToggle = async (companyId: number, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    const result = await togglePinCompany(companyId, newStatus);

    if (result) {
      // Update the company in the list immediately
      setCompanies(prevCompanies =>
        prevCompanies.map(company =>
          company.company_id === companyId
            ? { ...company, is_pinned: newStatus }
            : company
        )
      );
    }
  };

  // If viewing a specific company's dashboard
  if (selectedCompany) {
    return (
      <CompanyDashboard
        company={selectedCompany}
        onBack={handleBackToCompanies}
      />
    );
  }

  // Companies list view
  return (
    <div className="relative flex h-full flex-col bg-gray-50">
      <div className="border-b border-gray-200 bg-white px-6 py-6 shadow-sm">
        <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Companies
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {activeTab === "companies"
                ? "Select a company to view their dashboard and programs"
                : activeTab === "subscriptions"
                ? "Manage subscription terms, available credits, 12-month data retention, and client notifications"
                : "View and filter all programs across organizations"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard/company-management")}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Manage Companies
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 gap-6">
          <button
            onClick={() => setActiveTab("companies")}
            className={`pb-3 pt-1 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "companies"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <MdBusiness className="text-lg" />
            Company Directory
          </button>

          <button
            onClick={() => setActiveTab("subscriptions")}
            className={`pb-3 pt-1 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "subscriptions"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <MdAutorenew className="text-lg" />
            Manage Subscriptions
          </button>

          <button
            onClick={() => setActiveTab("programs")}
            className={`pb-3 pt-1 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "programs"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <ProgramsIcon className="h-4 w-4 stroke-current" />
            Programs
          </button>
        </div>

        {/* Search and Filters (Companies Tab Only) */}
        {activeTab === "companies" && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {/* Search Bar */}
          <div className="relative flex-1 min-w-[300px]">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Subscription Filter */}
          <select
            value={subscriptionFilter}
            onChange={(e) => {
              setSubscriptionFilter(e.target.value);
              if (e.target.value !== "custom") {
                setCustomSubscription("");
              }
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Subscriptions</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="half_yearly">Half Yearly</option>
            <option value="yearly">Yearly</option>
            <option value="pilot">Pilot</option>
            <option value="one-off">One-Off</option>
            <option value="none">No Subscription</option>
            <option value="custom">Custom...</option>
          </select>

          {/* Custom Subscription Input */}
          {subscriptionFilter === "custom" && (
            <input
              type="text"
              placeholder="Enter custom subscription..."
              value={customSubscription}
              onChange={(e) => setCustomSubscription(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          )}

          {/* Program Status Filter */}
          <select
            value={programStatusFilter}
            onChange={(e) => setProgramStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Programs</option>
            <option value="active">Active Programs</option>
            <option value="expired">Expired Programs</option>
            <option value="no_programs">No Programs</option>
          </select>

          {/* Archive Toggle */}
          <button
            onClick={() => setShowArchivedOnly(!showArchivedOnly)}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
              showArchivedOnly
                ? "border-orange-500 bg-orange-50 text-orange-700 hover:bg-orange-100"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            <MdArchive className="h-4 w-4" />
            {showArchivedOnly ? "Showing Archived" : "Show Archived"}
          </button>
        </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {activeTab === "subscriptions" ? (
          <OrganizationsSubscriptions />
        ) : activeTab === "programs" ? (
          <CompanyProgramsTableTab companies={companies} />
        ) : companiesLoading ? (
          // Skeleton loading state for better UX
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="animate-pulse rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-lg bg-gray-200"></div>
                  <div className="flex-1">
                    <div className="mb-2 h-5 w-3/4 rounded bg-gray-200"></div>
                    <div className="h-4 w-1/2 rounded bg-gray-200"></div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="h-4 w-full rounded bg-gray-200"></div>
                  <div className="h-4 w-2/3 rounded bg-gray-200"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (() => {
            // Priority: PINNED FIRST, then by closest start date (upcoming programs prioritized), NO PROGRAMS LAST
            const sortedCompanies = [...companies].sort((a, b) => {
              // PINNED companies always come first
              if (a.is_pinned && !b.is_pinned) return -1;
              if (!a.is_pinned && b.is_pinned) return 1;

              const now = new Date();

              // Get program dates for company A
              const aStartDate = earliestProgramStartDates[a.company_id]
                ? new Date(earliestProgramStartDates[a.company_id]!)
                : null;
              const aEndDate = latestProgramEndDates[a.company_id]
                ? new Date(latestProgramEndDates[a.company_id]!)
                : null;
              const aHasPrograms = programCounts[a.company_id] > 0;

              // Get program dates for company B
              const bStartDate = earliestProgramStartDates[b.company_id]
                ? new Date(earliestProgramStartDates[b.company_id]!)
                : null;
              const bEndDate = latestProgramEndDates[b.company_id]
                ? new Date(latestProgramEndDates[b.company_id]!)
                : null;
              const bHasPrograms = programCounts[b.company_id] > 0;

              // Companies with NO programs go to the bottom
              if (!aHasPrograms && bHasPrograms) return 1;
              if (aHasPrograms && !bHasPrograms) return -1;
              if (!aHasPrograms && !bHasPrograms) return 0; // Both have no programs

              // Determine if programs are upcoming or past
              const aIsUpcoming = aStartDate && aStartDate >= now;
              const bIsUpcoming = bStartDate && bStartDate >= now;

              // Priority 1: Companies with upcoming programs come first
              if (aIsUpcoming && !bIsUpcoming) return -1;
              if (!aIsUpcoming && bIsUpcoming) return 1;

              // Priority 2: Both upcoming - sort by closest start date
              if (aIsUpcoming && bIsUpcoming) {
                return aStartDate!.getTime() - bStartDate!.getTime();
              }

              // Priority 3: Both past/no upcoming - sort by end date status
              // Companies with active programs (not ended) come first
              const aIsActive = aEndDate && aEndDate >= now;
              const bIsActive = bEndDate && bEndDate >= now;

              if (aIsActive && !bIsActive) return -1;
              if (!aIsActive && bIsActive) return 1;

              // Priority 4: Both active - sort by closest end date
              if (aIsActive && bIsActive) {
                return aEndDate!.getTime() - bEndDate!.getTime();
              }

              // Priority 5: Both ended - sort by most recent end date (descending)
              if (aEndDate && bEndDate) {
                return bEndDate.getTime() - aEndDate.getTime();
              }

              // Priority 6: No dates - no sorting
              return 0;
            });

            // Apply all filters
            const filteredCompanies = sortedCompanies.filter(company => {
              // Subscription filter
              if (subscriptionFilter === "custom") {
                if (!customSubscription.trim()) return false;
                return company.subscription_type?.toLowerCase().includes(customSubscription.toLowerCase());
              } else if (subscriptionFilter !== "all") {
                if (subscriptionFilter === "none") {
                  if (company.subscription_type) return false;
                } else {
                  if (company.subscription_type !== subscriptionFilter) return false;
                }
              }

              // Program status filter
              if (programStatusFilter !== "all") {
                const programCount = programCounts[company.company_id] || 0;
                const latestEndDate = latestProgramEndDates[company.company_id];

                if (programStatusFilter === "no_programs") {
                  if (programCount > 0) return false;
                } else if (programStatusFilter === "active") {
                  if (!latestEndDate || new Date(latestEndDate) < new Date()) return false;
                } else if (programStatusFilter === "expired") {
                  if (!latestEndDate || new Date(latestEndDate) >= new Date()) return false;
                }
              }

              return true;
            });

            return filteredCompanies.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredCompanies.map(company => (
                  <CompanyCard
                    key={company.company_id}
                    company={company}
                    programCount={programCounts[company.company_id] || 0}
                    latestProgramEndDate={latestProgramEndDates[company.company_id] || null}
                    earliestProgramStartDate={earliestProgramStartDates[company.company_id] || null}
                    onClick={() => handleCompanyClick(company)}
                    onArchiveToggle={handleArchiveToggle}
                    onPinToggle={handlePinToggle}
                  />
                ))}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <MdBusiness className="mx-auto mb-4 h-16 w-16 text-gray-300" />
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">
                    No companies found
                  </h3>
                  <p className="text-gray-600">
                    {searchQuery || subscriptionFilter !== "all" || programStatusFilter !== "all" || showArchivedOnly
                      ? "Try adjusting your filters"
                      : "No companies have been created yet"}
                  </p>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
};

export default AdminCompanyPrograms;

